import type { CreatePriceRequest } from '@domains/subscription/priv/Billables/api/api'
import {
  type AssignedPriceMapper,
  mapAssignedPaymentFrequency,
  mapPaymentFrequencyToPriceFrequency,
  type PriceRequestMapper,
} from '@domains/subscription/priv/Billables/api/mappers'
import type { BaseAlgorithm } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type {
  AssignedPriceAlgorithm,
  MaxAlgorithm,
  PriceRequest,
  TemplateAssignedPrice,
  TemplatePriceRequest,
} from '@mynt/backoffice-api-spec/billables'
import type { MaxPrice, MaxPriceRequest } from './max'

export const isMax = <T extends BaseAlgorithm>(billable: unknown): billable is MaxPrice<T> => {
  return (billable as MaxPrice<T>)?.type === 'Max'
}

export const isMaxAssignedPrice = (price: TemplateAssignedPrice): price is MaxAssignedPrice => {
  return price.algorithm.type === 'Max'
}

export class MaxPriceRequestMapper<T extends CreatePriceRequest> implements PriceRequestMapper {
  supports(price: CreatePriceRequest): price is MaxPriceRequest<T> {
    return price.type === 'Max'
  }

  toAssignedPriceAlgorithm(price: CreatePriceRequest, mapper: (price: CreatePriceRequest) => PriceRequestMapper): AssignedPriceAlgorithm {
    if (!this.supports(price)) {
      throw new Error(`Unsupported price request type in MaxPriceRequestMapper:toAssignedPriceAlgorithm: ${price.type}`)
    }

    const leftMapper = mapper(price.lalgorithm)
    const rightMapper = mapper(price.ralgorithm)

    if (!leftMapper.supports(price.lalgorithm)) {
      throw new Error(`Unsupported left algorithm type in MaxPriceRequestMapper:toAssignedPriceAlgorithm: ${price.lalgorithm.type}`)
    }

    if (!rightMapper.supports(price.ralgorithm)) {
      throw new Error(`Unsupported right algorithm type in MaxPriceRequestMapper:toAssignedPriceAlgorithm: ${price.ralgorithm.type}`)
    }

    return {
      type: 'Max',
      lalgorithm: leftMapper.toAssignedPriceAlgorithm(price.lalgorithm, mapper),
      ralgorithm: rightMapper.toAssignedPriceAlgorithm(price.ralgorithm, mapper),
    }
  }

  toPriceRequestDto(price: CreatePriceRequest, algorithmMapper: (price: CreatePriceRequest) => PriceRequestMapper): TemplatePriceRequest {
    if (!this.supports(price)) {
      throw new Error(`Unsupported price request type in MaxPriceRequestMapper:toPriceRequestDto: ${price.type}`)
    }

    const leftMapper = algorithmMapper(price.lalgorithm)
    const rightMapper = algorithmMapper(price.ralgorithm)

    if (!leftMapper.supports(price.lalgorithm)) {
      throw new Error(`Unsupported left algorithm type in MaxPriceRequestMapper:toPriceRequestDto: ${price.lalgorithm.type}`)
    }

    if (!rightMapper.supports(price.ralgorithm)) {
      throw new Error(`Unsupported right algorithm type in MaxPriceRequestMapper:toPriceRequestDto: ${price.ralgorithm.type}`)
    }

    return {
      algorithm: {
        type: 'Max',
        lalgorithm: leftMapper.toAssignedPriceAlgorithm(price.lalgorithm, algorithmMapper),
        ralgorithm: rightMapper.toAssignedPriceAlgorithm(price.ralgorithm, algorithmMapper),
      },
      priceFrequency: mapPaymentFrequencyToPriceFrequency(price.paymentFrequency),
    }
  }
}

type MaxAssignedPrice = TemplateAssignedPrice & {
  algorithm: MaxAlgorithm
}

export class MaxAssignedPriceMapper implements AssignedPriceMapper {
  supports(assignedPrice: TemplateAssignedPrice) {
    return isMaxAssignedPrice(assignedPrice)
  }

  toBillablePrice(assignedPrice: TemplateAssignedPrice): MaxPrice<BaseAlgorithm> {
    if (!isMaxAssignedPrice(assignedPrice)) {
      throw new Error(`Unsupported assigned price type in MaxAssignedPriceMapper: ${assignedPrice.algorithm.type}`)
    }

    return {
      billableId: assignedPrice.billableId,
      type: 'Max',
      lalgorithm: assignedPrice.algorithm.lalgorithm,
      ralgorithm: assignedPrice.algorithm.ralgorithm,
      paymentFrequency: mapAssignedPaymentFrequency(assignedPrice.priceFrequency),
      supported: true,
    }
  }
}
